import { css } from '@emotion/react';
import { forwardRef } from 'react';

import type { SasIconProps } from './sas';
import { theme } from './theme';

const styles = {
  css: (color?: string, dimensions?: { width: string; height: string }) => css`
    svg {
      color: ${color ?? 'var(--color-icon)'};
      width: ${dimensions?.width ? dimensions.width : ''};
      height: ${dimensions?.height ? dimensions.height : ''};
    }
  `,

  button: (hitArea = 10) => css`
    cursor: pointer;

    > s4s-icon {
      width: 100%;
      height: 100%;
      padding: ${hitArea}px;
      margin: -${hitArea}px;
      box-sizing: content-box;
    }

    &:focus-visible {
      outline: none;

      s4s-icon {
        outline: 2px solid ${theme.colors.blue[1]};
        border-radius: 50%;
      }
    }

    @supports not selector(:focus-visible) {
      &:focus {
        outline: none;

        s4s-icon {
          outline: 2px solid ${theme.colors.blue[1]};
          border-radius: 50%;
        }
      }
    }
  `,
};

type IconProps = {
  className?: string;
  dimensions?: { width: string; height: string };
} & SasIconProps;

export function SasIcon(props: IconProps) {
  const { color, className, dimensions, ...rest } = props;

  return (
    <span css={styles.css(color, dimensions)} className={className}>
      <s4s-icon key={props.name} {...rest} />
    </span>
  );
}

type ButtonProps = {
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  hitArea?: number;
  disabled?: boolean;
} & IconProps;

export const SasIconButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { onClick, color, disabled, className = '', tabIndex, dimensions, ...rest } = props;

  return (
    <button
      ref={ref}
      onClick={onClick}
      css={[styles.button(props.hitArea), styles.css(color, dimensions)]}
      className={`reset ${className}`}
      disabled={disabled}
      tabIndex={tabIndex}
    >
      <s4s-icon key={props.name} {...rest} clickable={true} />
    </button>
  );
});

SasIconButton.displayName = 'SasIconButton';
